import { useRouter } from "expo-router";
import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bell,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  FileSignature,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Mail,
  Phone,
  Plus,
  WalletCards,
} from "../../../components/central-icons";
import { CentralInteractiveSurface } from "../../../components/CentralInteractiveSurface";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui";
import {
  CentralDashboardSummary,
  dismissCentralDashboardActions,
  getCentralDashboardErrorMessage,
  getCentralDashboardSummary,
  getCentralDeliveryProviderPresentation,
  rankCentralDashboardWork,
} from "../../../lib/central-dashboard";
import {
  getCentralFamilyJourneyReminderErrorMessage,
  remindCentralFamilyJourneyStage,
} from "../../../lib/central-family-journey";
import { central, semantic, spacing } from "../../../theme/tokens";
import { arrangementRoutes } from "../routes";
import { DataTable, SecondaryButton } from "./central-workspace-primitives";
import type { IconComponent } from "./central-workspace-primitives";

type MetricTone = "forest" | "blue" | "gold" | "red";

type DashboardMetric = {
  label: string;
  mobileLabel?: string;
  value: string;
  detail: string;
  tone: MetricTone;
  icon: IconComponent;
};

export type CaseBoardRow = {
  id: string;
  lovedOne: string;
  family: string;
  status: string;
  director: string;
  paperwork: string;
  signatures: string;
  balance: string;
  next: string;
  due: string;
  priority: "Critical" | "High" | "Normal";
};

const CENTRAL_FUNERAL_HOME_NAME = "Funeral home";

const quickActions = [
  {
    label: "New case",
    icon: Plus,
    href: arrangementRoutes.systemPage("create-case"),
  },
  {
    label: "Open cases",
    icon: FolderOpen,
    href: arrangementRoutes.systemPage("case-list"),
  },
  {
    label: "Forms",
    icon: FileText,
    href: arrangementRoutes.systemPage("generated-forms"),
  },
  {
    label: "Payment",
    icon: CreditCard,
    href: arrangementRoutes.systemPage("financials"),
  },
];

export function DashboardHome({
  isDesktop,
  useWideContentColumns,
}: {
  isDesktop: boolean;
  useWideContentColumns: boolean;
}) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const showQueueColumns = isDesktop && width >= 1200;
  const [summary, setSummary] = useState<CentralDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [dismissingIds, setDismissingIds] = useState<string[]>([]);
  const [dismissError, setDismissError] = useState("");
  const [remindingCaseId, setRemindingCaseId] = useState<string | null>(null);
  const [familyProgressMessage, setFamilyProgressMessage] = useState("");
  const [familyProgressError, setFamilyProgressError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setDashboardError("");
    try {
      setSummary(await getCentralDashboardSummary());
    } catch (caught) {
      setDashboardError(getCentralDashboardErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (loading) return <LoadingState label="Loading dashboard…" description="Reading live cases, queues, and attention items." />;
  if (dashboardError || !summary) return <ErrorState title="Dashboard could not load" description={dashboardError || "The live dashboard is unavailable."} action={{ label: "Retry loading", onPress: () => void load() }} />;
  const rankedWork = rankCentralDashboardWork(summary);
  const dismissWork = async (items: typeof rankedWork) => {
    const dismissible = items.filter((item) => item.kind === "action" && item.sourceId && item.version);
    if (!dismissible.length) return;
    const ids = dismissible.map((item) => item.id);
    setDismissingIds(ids);
    setDismissError("");
    try {
      await dismissCentralDashboardActions(dismissible.map((item) => ({ id: item.sourceId!, version: item.version! })));
      const dismissedSourceIds = new Set(dismissible.map((item) => item.sourceId));
      setSummary((current) => current ? { ...current, attention: current.attention.filter((item) => !dismissedSourceIds.has(item.id)) } : current);
    } catch (caught) {
      setDismissError(getCentralDashboardErrorMessage(caught));
    } finally {
      setDismissingIds([]);
    }
  };
  const liveMetrics: DashboardMetric[] = [
    { label: "Open cases", value: String(summary.counts.openCases), detail: `${summary.counts.casesNeedingReview} need review`, tone: "blue", icon: FolderOpen },
    { label: "First calls", value: String(summary.counts.firstCalls), detail: summary.counts.firstCalls ? "Review and assign" : "No calls queued", tone: "blue", icon: Phone },
    { label: "Signature packets", mobileLabel: "Signatures", value: String(summary.counts.signaturePackets), detail: `${summary.counts.formsWaitingForSignature} waiting`, tone: "blue", icon: FileSignature },
    { label: "Outstanding balances", mobileLabel: "Balances", value: summary.financial ? formatDashboardMoney(summary.financial.revenueAtRiskCents) : "—", detail: summary.financial ? "Across open cases" : "Director access required", tone: "blue", icon: WalletCards },
  ];
  const sendFamilyReminder = async (followUp: CentralDashboardSummary["familyFollowUps"][number]) => {
    if (!followUp.canRemind || followUp.stage === "intake") return;
    setRemindingCaseId(followUp.caseId);
    setFamilyProgressError("");
    setFamilyProgressMessage("");
    try {
      const result = await remindCentralFamilyJourneyStage({
        caseId: followUp.caseId,
        reviewedSubmissionRevision: followUp.reviewedSubmissionRevision,
        stage: followUp.stage,
      });
      setFamilyProgressMessage(result.sent
        ? `${followUp.stageLabel} reminder accepted for delivery to ${followUp.familyName || "the family"}.`
        : "A reminder is already being sent for this family step.");
      await load();
    } catch (caught) {
      setFamilyProgressError(getCentralFamilyJourneyReminderErrorMessage(caught));
    } finally {
      setRemindingCaseId(null);
    }
  };
  return (
    <View accessibilityLabel={CENTRAL_FUNERAL_HOME_NAME} style={styles.pageStack}>
      <View style={styles.dashboardOperationalHeader}>
        <Text accessibilityRole="header" style={styles.dashboardTitle}>Today</Text>
      </View>
      <QuickActions compact />
      {rankedWork.length ? (
        <View accessibilityLabel="Ranked next actions" nativeID="central-ranked-next-actions">
        <View style={styles.workQueue}>
          <View style={styles.workQueueToolbar}>
            <View style={styles.workQueueHeadingGroup}>
              <View style={styles.workQueueTitleRow}>
                <ClipboardCheck color={central.action.primary} size={18} strokeWidth={2.2} />
                <Text accessibilityRole="header" style={styles.workQueueTitle}>Next actions</Text>
                <StatusPill label={String(rankedWork.length)} tone="neutral" />
              </View>
            </View>
          </View>
          <View style={styles.workQueueRows}>
              {dismissError ? <Text accessibilityLiveRegion="polite" style={styles.dismissError}>{dismissError}</Text> : null}
              {showQueueColumns ? (
                <View accessibilityRole="none" style={styles.workQueueColumnHeader}>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueuePriorityColumn]}>Priority</Text>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueueTaskColumn]}>Task</Text>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueueCaseColumn]}>Case</Text>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueueOwnerColumn]}>Owner</Text>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueueReceivedColumn]}>Received</Text>
                  <Text style={[styles.workQueueColumnLabel, styles.workQueueActionColumn]}>Actions</Text>
                </View>
              ) : null}
              {rankedWork.slice(0, 8).map((item) => (
                <CentralInteractiveSurface
                  accessibilityHint="Opens this next action"
                  accessibilityLabel={`${item.title}${item.caseName ? ` for ${item.caseName}` : ""}: ${item.detail}. Assigned to ${item.assignedTo}.${item.dueAt ? ` Due ${formatDashboardTime(item.dueAt)}.` : ""}`}
                  contentStyle={[styles.workQueueRowContent, showQueueColumns ? styles.workQueueRowContentDesktop : styles.workQueueRowContentMobile]}
                  focusedStyle={styles.alertCardFocused}
                  footer={(
                    <>
                      <View pointerEvents="none" style={styles.alertOpen}>
                        <Text style={styles.alertOpenText}>{item.kind === "queue" ? `Open ${item.count}` : "Open"}</Text>
                        <ArrowRight color={central.action.primary} size={13} strokeWidth={2.2} />
                      </View>
                      {item.kind === "action" ? <Pressable accessibilityRole="button" disabled={dismissingIds.includes(item.id)} onPress={() => void dismissWork([item])} style={({ pressed }) => [styles.dismissAction, pressed ? styles.dismissActionPressed : null]}><CheckCircle2 color={central.text.secondary} size={14} /><Text style={styles.dismissActionText}>{dismissingIds.includes(item.id) ? "Dismissing…" : "Dismiss"}</Text></Pressable> : null}
                    </>
                  )}
                  footerStyle={[styles.alertActions, showQueueColumns ? styles.alertActionsDesktop : null]}
                  key={item.id}
                  onPress={() => router.push(item.href as never)}
                  pressedStyle={styles.alertCardPressed}
                  style={[styles.workQueueRow, showQueueColumns ? styles.workQueueRowDesktop : styles.workQueueRowMobile]}
                  testID={`central-next-action-${item.id}`}
                >
                  {showQueueColumns ? (
                    <>
                      <View style={styles.workQueuePriorityColumn}>
                        <StatusPill label={item.kind === "queue" ? `${item.priority} · ${item.count}` : item.priority} tone={item.priority === "Critical" ? "red" : item.priority === "High" ? "gold" : "forest"} />
                      </View>
                      <View style={styles.workQueueTaskColumn}>
                        <View style={styles.alertTitleRow}>
                          {item.priority === "Critical" || item.priority === "High" ? (
                            <AlertTriangle color={item.priority === "Critical" ? semantic.status.error.foreground : semantic.status.warning.foreground} size={15} strokeWidth={2.2} />
                          ) : null}
                          <Text style={styles.alertTitle}>{item.title}</Text>
                        </View>
                        <Text style={styles.alertDetail}>{item.detail}{formatDashboardDue(item)}</Text>
                      </View>
                      <Text style={[styles.alertCaseName, styles.workQueueCaseColumn]}>{item.caseName ?? "Multiple cases"}</Text>
                      <Text style={[styles.alertDetail, styles.workQueueOwnerColumn]}>{item.assignedTo}</Text>
                      <Text style={[styles.alertDates, styles.workQueueReceivedColumn]}>{formatDashboardReceivedAt(item)}</Text>
                    </>
                  ) : (
                    <>
                      <View style={styles.alertHeader}>
                        <StatusPill label={item.kind === "queue" ? `${item.priority} · ${item.count}` : item.priority} tone={item.priority === "Critical" ? "red" : item.priority === "High" ? "gold" : "forest"} />
                        <Text style={styles.alertDates}>{formatDashboardReceivedAt(item)}</Text>
                      </View>
                      <View style={styles.alertTitleRow}>
                      {item.priority === "Critical" || item.priority === "High" ? (
                        <AlertTriangle
                          color={item.priority === "Critical" ? semantic.status.error.foreground : semantic.status.warning.foreground}
                          size={16}
                          strokeWidth={2.2}
                        />
                      ) : null}
                      <Text style={styles.alertTitle}>{item.title}</Text>
                      </View>
                      <Text style={styles.alertDetail}>{item.detail}{formatDashboardDue(item)}</Text>
                      <View style={styles.alertMetadata}>
                        <Text style={styles.alertCaseName}>{item.caseName ? `Case: ${item.caseName}` : "Multiple cases"}</Text>
                        <Text style={styles.alertDetail}>{item.assignedTo}</Text>
                      </View>
                    </>
                  )}
                </CentralInteractiveSurface>
              ))}
          </View>
          <View style={styles.dismissAllFooter}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Dismiss all next actions"
              disabled={Boolean(dismissingIds.length)}
              onPress={() => void dismissWork(rankedWork)}
              style={({ pressed }) => [
                styles.dismissAllAction,
                pressed ? styles.dismissActionPressed : null,
                dismissingIds.length ? styles.buttonDisabled : null,
              ]}
            >
              <Text style={styles.dismissAllActionText}>{dismissingIds.length ? "Dismissing…" : "Dismiss All"}</Text>
            </Pressable>
          </View>
        </View>
        </View>
      ) : null}
      <View style={[styles.dashboardLayout, useWideContentColumns ? null : styles.stack]}>
        <View style={[
          styles.dashboardMainColumn,
          useWideContentColumns ? styles.dashboardMainColumnDesktop : styles.dashboardMainColumnCompact,
        ]}>
          <View nativeID="central-dashboard-metrics" style={[styles.metricGrid, isDesktop ? null : styles.metricGridMobile]}>{liveMetrics.map((metric) => <MetricTile key={metric.label} metric={metric} compact={!isDesktop} />)}</View>
          <Panel title="Family progress" icon={Mail}>
            {familyProgressMessage ? <Text accessibilityLiveRegion="polite" style={styles.successText}>{familyProgressMessage}</Text> : null}
            {familyProgressError ? <Text accessibilityLiveRegion="polite" style={styles.dismissError}>{familyProgressError}</Text> : null}
            {summary.familyFollowUps.length ? (
              <FamilyProgressList
                followUps={summary.familyFollowUps}
                isDesktop={isDesktop}
                onOpen={(href) => router.push(href as never)}
                onRemind={(followUp) => void sendFamilyReminder(followUp)}
                remindingCaseId={remindingCaseId}
              />
            ) : <EmptyState title="Family plans are current" description="Incomplete Planner steps will appear here after a family starts a case." />}
          </Panel>
          <Panel title="Active case board" icon={LayoutDashboard} actionLabel="View all" actionHref={arrangementRoutes.systemPage("case-list")}>
            {summary.priorityCases.length ? <CaseBoard isDesktop={isDesktop} rows={summary.priorityCases} /> : <EmptyState title="No open cases" description="New cases will appear here when the first call is recorded." action={{ label: "Create case", onPress: () => router.push(arrangementRoutes.systemPage("create-case") as never) }} />}
          </Panel>
          <View style={[styles.twoColumn, isDesktop ? null : styles.stack]}>
            <Panel title="Operations this week" icon={CalendarDays}>
              <View style={styles.moneyGrid}>
                <MoneyRow label="Services this week" value={String(summary.counts.servicesThisWeek)} />
                <MoneyRow label="Callbacks pending" value={String(summary.attention.filter((item) => item.type === "callback").length)} />
                <MoneyRow label="Missing documents" value={String(summary.counts.missingDocuments)} />
                <MoneyRow label="Payments pending" value={String(summary.counts.paymentsPending)} />
              </View>
            </Panel>
            <Panel title="Recent activity" icon={Bell}>
              {summary.recentActivity.length ? summary.recentActivity.map((item) => <Pressable key={item.id} accessibilityRole="button" accessibilityLabel={`Open ${item.label}`} onPress={() => router.push(`/workspace/case-overview?caseId=${encodeURIComponent(item.caseId)}` as never)} style={styles.activityRow}><View style={styles.activityDot} /><View style={styles.timelineCopy}><Text style={styles.timelineTitle}>{item.label}</Text><Text style={styles.timelineMeta}>{formatDashboardTime(item.time)}</Text></View></Pressable>) : <EmptyState title="No recent activity" description="Live case actions will appear here." />}
            </Panel>
          </View>
        </View>
        <View style={[styles.dashboardSideColumn, useWideContentColumns ? styles.dashboardSideColumnDesktop : styles.dashboardSideColumnCompact]}>
          <Panel title="Financial snapshot" icon={WalletCards}>
            <View style={styles.moneyGrid}>
              <MoneyRow label="Statements in review" value={summary.financial ? formatDashboardMoney(summary.financial.statementsInReviewCents) : "—"} />
              <MoneyRow label="Unsigned balances" value={summary.financial ? formatDashboardMoney(summary.financial.unsignedBalancesCents) : "—"} />
              <MoneyRow label="Outstanding balances" value={summary.financial ? formatDashboardMoney(summary.financial.revenueAtRiskCents) : "—"} />
              <MoneyRow label="Payments pending" value={String(summary.counts.paymentsPending)} />
            </View>
          </Panel>
          <Panel title="Family message delivery" icon={Bell}>
            <View style={styles.moneyGrid}>{summary.deliveryProviders.map((provider) => <MoneyRow key={provider.channel} label={provider.channel === "email" ? "Email" : "Text Messages"} value={getCentralDeliveryProviderPresentation(provider).label} />)}</View>
          </Panel>
          <Panel title="Reports" icon={BarChart3}>
            {summary.reportLinks.length ? <View style={styles.alertStack}>{summary.reportLinks.map((report) => <Pressable key={report.id} accessibilityRole="button" accessibilityLabel={`Open ${report.label}`} onPress={() => router.push(report.href as never)} style={styles.alertCard}><View style={styles.alertHeader}><Text style={styles.alertTitle}>{report.label}</Text><ArrowRight color={central.action.primary} size={13} strokeWidth={2.2} /></View></Pressable>)}</View> : <EmptyState title="No reports available" description="Reports are limited by your current role." />}
          </Panel>
        </View>
      </View>
    </View>
  );
}

function FamilyProgressList({ followUps, isDesktop, onOpen, onRemind, remindingCaseId }: {
  followUps: CentralDashboardSummary["familyFollowUps"];
  isDesktop: boolean;
  onOpen: (href: string) => void;
  onRemind: (followUp: CentralDashboardSummary["familyFollowUps"][number]) => void;
  remindingCaseId: string | null;
}) {
  return (
    <View style={styles.familyProgressList}>
      {followUps.map((followUp) => {
        const status = followUp.progress === "ready_for_review"
          ? "Ready for staff review"
          : followUp.canRemind
            ? followUp.progress === "saved" ? "Family working" : "Waiting for family"
            : "Prior step needs review";
        const progress = followUp.totalSections
          ? `${followUp.completedSections} of ${followUp.totalSections} planning areas saved`
          : status;
        return (
          <View accessible accessibilityLabel={`${followUp.caseName}. ${followUp.stageLabel}. ${status}. ${progress}.`} key={followUp.caseId} style={styles.familyProgressRow}>
            <Pressable accessibilityRole="button" accessibilityLabel={`Open ${followUp.caseName} family progress`} onPress={() => onOpen(followUp.href)} style={({ pressed }) => [styles.familyProgressCopy, pressed ? styles.alertCardPressed : null]}>
              <View style={styles.alertTitleRow}>
                <Text style={styles.alertTitle}>{followUp.caseName}</Text>
                <StatusPill label={status} tone={followUp.progress === "ready_for_review" ? "gold" : followUp.canRemind ? "blue" : "neutral"} />
              </View>
              <Text style={styles.alertDetail}>Current family step: {followUp.stageLabel}</Text>
              <Text style={styles.alertDetail}>{followUp.nextSectionLabel ? `Next: ${followUp.nextSectionLabel} · ` : ""}{progress}</Text>
            </Pressable>
            {followUp.canRemind ? (
              <SecondaryButton
                label={remindingCaseId === followUp.caseId ? "Sending…" : "Send Reminder"}
                icon={Mail}
                disabled={remindingCaseId !== null}
                fullWidth={!isDesktop}
                onPress={() => onRemind(followUp)}
              />
            ) : <SecondaryButton label={followUp.progress === "ready_for_review" ? "Review Step" : "Open Case"} icon={ArrowRight} fullWidth={!isDesktop} onPress={() => onOpen(followUp.href)} />}
          </View>
        );
      })}
    </View>
  );
}

function formatDashboardMoney(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatDashboardTime(value: string | null) {
  if (!value) return "Just now";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Recent" : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatDashboardReceivedAt(item: ReturnType<typeof rankCentralDashboardWork>[number]) {
  const value = item.receivedAt ?? item.updatedAt;
  return value ? formatDashboardTime(value) : "Not recorded";
}

function formatDashboardDue(item: ReturnType<typeof rankCentralDashboardWork>[number]) {
  return item.dueAt ? ` · Due ${formatDashboardTime(item.dueAt)}` : "";
}

function QuickActions({ compact = false }: { compact?: boolean }) {
  const router = useRouter();
  const [highlightedAction, setHighlightedAction] = useState<string | null>(null);

  return (
    <View style={[styles.quickActionGrid, compact ? styles.quickActionGridCompact : null]}>
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Pressable
              key={action.label}
              accessibilityRole="button"
              accessibilityLabel={action.label}
              onBlur={() => setHighlightedAction(null)}
              onFocus={() => setHighlightedAction(action.label)}
              onHoverIn={() => setHighlightedAction(action.label)}
              onHoverOut={() => setHighlightedAction(null)}
              onPress={() => router.push(action.href as never)}
              style={({ pressed }) => [
                styles.quickAction,
                compact ? styles.quickActionCompact : null,
                highlightedAction === action.label ? styles.quickActionHighlighted : null,
                pressed ? styles.quickActionPressed : null,
              ]}
            >
              <Icon color={central.action.primary} size={compact ? 16 : 18} strokeWidth={2} />
              <Text style={[styles.quickActionText, compact ? styles.quickActionTextCompact : null]}>{action.label}</Text>
            </Pressable>
          );
        })}
    </View>
  );
}

function CaseBoard({ isDesktop, rows: liveRows }: { isDesktop: boolean; rows?: CaseBoardRow[] }) {
  const router = useRouter();
  const sourceRows = liveRows ?? [];
  const rows = sourceRows.map((row) => [
    row.lovedOne,
    row.status,
    row.director,
    row.paperwork,
    row.balance,
    row.next,
    row.due,
  ]);
  return (
    <DataTable
      columns={[
        "Loved one",
        "Status",
        "Owner",
        "Paperwork",
        "Balance",
        "Next",
        "Due",
      ]}
      rows={rows}
      isDesktop={isDesktop}
      routeMode="none"
      onRowPress={(_row, index) => {
        const source = sourceRows[index];
        if (source?.id) {
          router.push(`/workspace/case-overview?caseId=${encodeURIComponent(source.id)}` as never);
        }
      }}
    />
  );
}

function MetricTile({
  metric,
  compact = false,
}: {
  metric: DashboardMetric;
  compact?: boolean;
}) {
  const Icon = metric.icon;
  const displayLabel = compact && metric.mobileLabel ? metric.mobileLabel : metric.label;
  return (
    <View accessible accessibilityRole="text" accessibilityLabel={`${toTitleCase(displayLabel)}: ${metric.value}. ${metric.detail}`} style={[styles.metricTile, compact ? styles.metricTileCompact : null, metricToneStyle(metric.tone)]}>
      <View style={[styles.metricHeader, compact ? styles.metricHeaderCompact : null]}>
        <View style={[styles.metricIcon, compact ? styles.metricIconCompact : null]}>
          <Icon color={central.action.primary} size={compact ? 15 : 18} strokeWidth={2} />
        </View>
        <Text style={[styles.metricLabel, compact ? styles.metricLabelCompact : null]} numberOfLines={2}>
          {toTitleCase(displayLabel)}
        </Text>
      </View>
      <Text style={[styles.metricValue, compact ? styles.metricValueCompact : null]}>{metric.value}</Text>
      <Text style={[styles.metricDetail, compact ? styles.metricDetailCompact : null]}>{metric.detail}</Text>
    </View>
  );
}

function Panel({
  title,
  icon: Icon,
  actionLabel,
  actionHref,
  compact = false,
  children,
}: {
  title: string;
  icon: IconComponent;
  actionLabel?: string;
  actionHref?: string;
  compact?: boolean;
  children: ReactNode;
}) {
  const router = useRouter();

  return (
    <View style={[styles.panel, compact ? styles.panelCompact : null]}>
      <View style={styles.panelHeader}>
        <View style={styles.panelTitleRow}>
          <Icon color={central.action.primary} size={18} strokeWidth={2} />
          <Text style={styles.panelTitle}>{title}</Text>
        </View>
        {actionLabel && actionHref ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={actionLabel}
            onPress={() => router.push(actionHref as never)}
            style={styles.panelAction}
          >
            <Text style={styles.panelActionText}>{actionLabel}</Text>
            <ArrowRight color={central.action.primary} size={13} strokeWidth={2.2} />
          </Pressable>
        ) : null}
      </View>
      {children}
    </View>
  );
}

function StatusPill({
  label,
  tone,
}: {
  label: string;
  tone: MetricTone | "neutral";
}) {
  return (
    <View style={[styles.statusPill, statusPillStyle(tone)]}>
      <Text style={styles.statusPillText}>{label}</Text>
    </View>
  );
}

function MoneyRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.moneyRow}>
      <Text style={styles.moneyLabel}>{label}</Text>
      <Text style={styles.moneyValue}>{value}</Text>
    </View>
  );
}

function toTitleCase(value: string) {
  return value.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

function metricToneStyle(tone: MetricTone) {
  if (tone === "red") return styles.metricRed;
  if (tone === "gold") return styles.metricGold;
  if (tone === "blue") return styles.metricBlue;
  return styles.metricForest;
}

function statusPillStyle(tone: MetricTone | "neutral") {
  if (tone === "red") return styles.statusRed;
  if (tone === "gold") return styles.statusGold;
  if (tone === "blue") return styles.statusBlue;
  if (tone === "forest") return styles.statusForest;
  return styles.statusNeutral;
}

const styles = StyleSheet.create({
  pageStack: {
    gap: spacing.md,
  },
  dashboardOperationalHeader: {
    alignItems: "flex-start",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  dashboardTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 24,
    lineHeight: 31,
  },
  dashboardLayout: {
    flexDirection: "row",
    gap: spacing.md,
    alignItems: "flex-start",
  },
  dashboardMainColumn: {
    minWidth: 0,
    gap: spacing.md,
  },
  dashboardMainColumnCompact: {
    width: "100%",
  },
  dashboardMainColumnDesktop: {
    flex: 1.75,
  },
  dashboardSideColumn: {
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.md,
  },
  dashboardSideColumnDesktop: {
    flex: 0.82,
    minWidth: 300,
  },
  dashboardSideColumnCompact: {
    width: "100%",
    minWidth: 0,
  },
  stack: {
    flexDirection: "column",
  },
  twoColumn: {
    flexDirection: "row",
    gap: spacing.md,
    alignItems: "stretch",
  },
  metricGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  metricGridMobile: {
    gap: 8,
  },
  metricTile: {
    flex: 1,
    minWidth: 160,
    minHeight: 104,
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: central.radius.card,
    borderWidth: 1,
  },
  metricTileCompact: {
    minWidth: 132,
    minHeight: 78,
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  metricForest: {
    backgroundColor: central.surface.muted,
    borderColor: central.border.subtle,
  },
  metricBlue: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
  },
  metricGold: {
    backgroundColor: central.accent.soft,
    borderColor: "#F5D1B1",
  },
  metricRed: {
    backgroundColor: central.surface.raised,
    borderColor: central.border.subtle,
  },
  metricHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  metricHeaderCompact: {
    gap: 6,
  },
  metricIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: central.surface.muted,
  },
  metricIconCompact: {
    width: 24,
    height: 24,
    borderRadius: 6,
  },
  metricLabel: {
    flex: 1,
    color: central.text.secondary,
    fontFamily: central.typography.label,
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  metricLabelCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
  metricValue: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 28,
    lineHeight: 34,
  },
  metricValueCompact: {
    fontSize: 24,
    lineHeight: 28,
  },
  metricDetail: {
    color: central.text.secondary,
    fontFamily: central.typography.bodyMedium,
    fontSize: 12,
    lineHeight: 18,
  },
  metricDetailCompact: {
    fontSize: 11,
    lineHeight: 15,
  },
  panel: {
    minWidth: 0,
    maxWidth: "100%",
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: central.radius.card,
    backgroundColor: central.surface.raised,
    borderWidth: 1,
    borderColor: central.border.subtle,
  },
  panelCompact: {
    gap: 9,
    padding: 10,
  },
  panelHeader: {
    minHeight: 32,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  panelTitleRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  panelTitle: {
    flexShrink: 1,
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 15,
    lineHeight: 20,
  },
  panelAction: {
    minHeight: 28,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  panelActionText: {
    color: central.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  statusPill: {
    flexShrink: 0,
    minHeight: 23,
    justifyContent: "center",
    borderRadius: 6,
    paddingHorizontal: 8,
  },
  statusPillText: {
    color: central.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 14,
  },
  statusRed: {
    backgroundColor: central.status.error.background,
  },
  statusGold: {
    backgroundColor: central.status.warning.background,
  },
  statusBlue: {
    backgroundColor: central.status.info.background,
  },
  statusForest: {
    backgroundColor: central.status.success.background,
  },
  statusNeutral: {
    backgroundColor: central.status.neutral.background,
  },
  moneyGrid: {
    gap: 8,
  },
  moneyRow: {
    minHeight: 36,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  moneyLabel: {
    flex: 1,
    color: central.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 17,
  },
  moneyValue: {
    color: central.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  quickActionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  quickActionGridCompact: {
    gap: 7,
  },
  quickAction: {
    flex: 1,
    minWidth: 112,
    minHeight: 54,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 11,
    borderRadius: 8,
    backgroundColor: central.surface.muted,
    borderWidth: 1,
    borderColor: central.border.subtle,
  },
  quickActionCompact: {
    minWidth: 76,
    minHeight: 44,
    gap: 6,
    paddingHorizontal: 8,
  },
  quickActionHighlighted: {
    backgroundColor: central.surface.raised,
    borderColor: central.action.primary,
  },
  quickActionPressed: {
    opacity: 0.82,
    transform: [{ translateY: 1 }],
  },
  quickActionText: {
    color: central.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
  },
  quickActionTextCompact: {
    fontSize: 11,
    lineHeight: 15,
  },
  workQueue: {
    overflow: "hidden",
    borderRadius: central.radius.card,
    borderWidth: 1,
    borderColor: central.border.subtle,
    backgroundColor: central.surface.raised,
  },
  workQueueToolbar: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.md,
    minHeight: 56,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  workQueueHeadingGroup: {
    flex: 1,
    gap: 3,
    minWidth: 0,
  },
  workQueueTitleRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
  },
  workQueueTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 16,
    lineHeight: 22,
  },
  workQueueColumnHeader: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.sm,
    minHeight: 38,
    paddingHorizontal: spacing.md,
    backgroundColor: central.surface.subtle,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  workQueueColumnLabel: {
    color: central.text.muted,
    fontFamily: central.typography.label,
    fontSize: 10,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  workQueueRows: {
    width: "100%",
  },
  workQueueRow: {
    backgroundColor: central.surface.raised,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  workQueueRowDesktop: {
    alignItems: "stretch",
    flexDirection: "row",
    minHeight: 76,
    paddingHorizontal: spacing.md,
  },
  workQueueRowMobile: {
    minHeight: 156,
    padding: spacing.md,
  },
  workQueueRowContent: {
    minWidth: 0,
  },
  workQueueRowContentDesktop: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  workQueueRowContentMobile: {
    gap: spacing.xs,
  },
  workQueuePriorityColumn: {
    flexBasis: 92,
    flexGrow: 0,
    flexShrink: 0,
  },
  workQueueTaskColumn: {
    flex: 2,
    gap: 2,
    minWidth: 160,
  },
  workQueueCaseColumn: {
    flex: 1.15,
    minWidth: 110,
  },
  workQueueOwnerColumn: {
    flex: 0.85,
    minWidth: 86,
  },
  workQueueReceivedColumn: {
    flex: 1.05,
    minWidth: 112,
  },
  workQueueActionColumn: {
    flexBasis: 154,
    flexGrow: 0,
    flexShrink: 0,
    textAlign: "right",
  },
  dismissError: {
    color: semantic.status.error.foreground,
    fontFamily: central.typography.label,
    fontSize: 11,
  },
  dismissAllFooter: {
    alignItems: "flex-end",
    borderTopColor: central.border.subtle,
    borderTopWidth: 1,
    paddingHorizontal: spacing.md,
  },
  dismissAllAction: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    paddingHorizontal: spacing.xs,
  },
  dismissAllActionText: {
    color: central.text.muted,
    fontFamily: central.typography.label,
    fontSize: 11,
    lineHeight: 16,
  },
  dismissAction: {
    alignItems: "center",
    flexDirection: "row",
    gap: 5,
    minHeight: 44,
    paddingHorizontal: spacing.xs,
  },
  dismissActionPressed: {
    opacity: 0.68,
  },
  dismissActionText: {
    color: central.text.secondary,
    fontFamily: central.typography.label,
    fontSize: 11,
  },
  successText: {
    color: semantic.status.success.foreground,
    fontFamily: central.typography.label,
    fontSize: 11,
  },
  buttonDisabled: {
    opacity: 0.48,
  },
  familyProgressList: {
    gap: spacing.xs,
  },
  familyProgressRow: {
    alignItems: "center",
    backgroundColor: central.surface.muted,
    borderColor: central.border.subtle,
    borderRadius: central.radius.card,
    borderWidth: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
    minHeight: 76,
    padding: spacing.sm,
  },
  familyProgressCopy: {
    flex: 1,
    gap: 3,
    minWidth: 240,
    padding: spacing.xs,
  },
  alertStack: {
    gap: 9,
  },
  alertCard: {
    padding: spacing.md,
    borderRadius: 8,
    backgroundColor: central.surface.muted,
    borderWidth: 1,
    borderColor: central.border.subtle,
  },
  alertCardFocused: {
    borderColor: semantic.focus.ring,
    shadowColor: semantic.focus.ring,
    shadowOpacity: 0.22,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  },
  alertCardPressed: {
    opacity: 0.82,
  },
  alertHeader: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  alertTitle: {
    flex: 1,
    minWidth: 0,
    color: central.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
  },
  alertTitleRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  alertDetail: {
    color: central.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
  alertMetadata: {
    alignItems: "baseline",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
  },
  alertDates: {
    color: central.text.muted,
    fontFamily: central.typography.body,
    fontSize: 10,
    lineHeight: 15,
  },
  alertCaseName: {
    color: central.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    lineHeight: 16,
  },
  alertOpen: {
    alignItems: "center",
    flexDirection: "row",
    gap: 4,
    marginTop: 2,
    minHeight: 44,
  },
  alertOpenText: {
    color: central.action.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
  },
  alertActions: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.md,
    justifyContent: "space-between",
  },
  alertActionsDesktop: {
    flexBasis: 154,
    flexGrow: 0,
    flexShrink: 0,
    flexWrap: "nowrap",
    justifyContent: "flex-end",
  },
  activityRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 9,
    paddingVertical: 6,
  },
  activityDot: {
    backgroundColor: central.action.primary,
    borderRadius: 5,
    height: 9,
    width: 9,
  },
  timelineCopy: {
    flex: 1,
    minWidth: 0,
  },
  timelineTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
  },
  timelineMeta: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
});
